import { useForm, Controller } from 'react-hook-form';
import { FormProvider, TextField, Table, Checkbox, SelectTag } from '../../../../../../components';
import { Button, Container, Paper, Stack, Typography, Select, MenuItem, Dialog, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request } from '../../../../../../utils';
import { toast } from 'react-toastify';
import { useCallback } from 'react';
import { dropdownFetch } from '../../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../../components';

const TABLE_COLUMNS = [
  {
    header: 'Group Name',
    accessor: (row) => row.groupname.groupname
  },
  {
    header: 'SubGroup Name ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.subgroupname}`}
        </span>
      );
    }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

const initialSubGroupMasterDataObj = {
  data: [],
  loading: false
};

export const SubGroupMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [subGroupMasterDataObj, setSubGroupMasterDataObj] = useState(initialSubGroupMasterDataObj);
  const [loading, setLoading] = useState(false);
  const [groupDropdown, setGroupDropdown] = useState([]);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        groupname: Yup.string().required('Group Name is required'),
        subgroupname: Yup.string().required('Sub Group Name  is required')
      })
    ),
    defaultValues: {
      groupname: '',
      subgroupname: ''
    },
    mode: 'all'
  });

  const { handleSubmit, setValue, reset } = methods;

  const getGroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('group-master');
    if (!selectedRecord) {
      const [defaultGroup] = data.filter(({ status }) => status === 'default');
      setValue('groupname', defaultGroup?._id);
    }
    setGroupDropdown(data);
  }, []);

  useEffect(() => {
    getGroupDropdown();
  }, [getGroupDropdown]);

  useEffect(() => {
    if (selectedRecord) {
      setValue('groupname', selectedRecord.groupname);
      setValue('subgroupname', selectedRecord.subgroupname);
      setValue('default', selectedRecord.status === 'default');
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/subgroup-master-update' : '/subgroup-master-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        groupname: formValues.groupname,
        subgroupname: formValues.subgroupname,
        status: formValues.default ? 'default' : 'active'
      }
    });
    if (response?.success) {
      toast.success(`Successfully ${editFlag ? 'edited' : 'created'} Sub Group master`);
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast.error(`Failed to ${editFlag ? 'edit' : 'create'} Sub Group master`);
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/subgroup-master-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully Cancelled Sub Group master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to Cancelled Sub Group master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/subgroup-master-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Sub Group master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Sub Group master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setSubGroupMasterDataObj({ loading: true });
    const response = await request('/subgroup-master-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setSubGroupMasterDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setSubGroupMasterDataObj({ loading: false });
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Sub Group Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Stack gap={0.1}>
              <Box
                sx={{
                  border: '0px solid gray',
                  padding: 2,
                  borderRadius: '8px',
                  width: '70%',
                  boxShadow: '0px 2px 10px #0099ff'
                }}
              >
                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.15, fontSize: '1.4rem', fontWeight: 700 }}>Group Name</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.7, border: currentAction ? '0.5px solid red' : '' }}
                    name="groupname"
                    menus={groupDropdown.map((data) => ({ ...data, name: data.groupname }))}
                  />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ flex: 0.17, fontSize: '1.4rem', fontWeight: 700 }}>SubGroup Name </Typography>
                  <TextField
                    name="subgroupname"
                    defaultValue=""
                    sx={{ flex: 0.68, border: currentAction ? '0.5px solid red' : '' }}
                    placeholder="Enter Sub Group Name"
                    disabled={!['new', 'edit'].includes(currentAction)}
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.25, fontSize: '1.4rem', fontWeight: 700 }}>Default</Typography>
                    <Checkbox disabled={!['new', 'edit'].includes(currentAction)} name="default" fontSize={18} />
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 1.5 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Sub Group Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={subGroupMasterDataObj.data || []}
          loading={subGroupMasterDataObj.loading}
          onRowClick={(row) => {
            const { groupname, ...otherDetails } = row;
            setSelectedRecord({
              ...otherDetails,
              groupname: groupname?._id
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
